import type * as DomainValues from '@/generated/core-enums'
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import {
  getClinicSettings,
  getSessionSnapshot,
  login,
  logout,
  updateClinicSettings,
  updateAccountLanguage,
  switchActiveClinic,
  createOrganization,
  activateDeveloperElevation,
  deactivateDeveloperElevation,
  type LoginInput,
  type CreateOrganizationInput,
} from '@/shared/api/coreClient'
import { useResourceReload } from '@/shared/api/resourceReload'
import type { Locale } from '@/shared/i18n/locale'
import { browserSessionContext, notifySessionContextChange } from '@/shared/api/sessionContext'

import { hasOrganization, type AccountSession, type ClinicSettings, type UpdateClinicSettingsInput } from './types'

export const SESSION_QUERY_KEY = ['session'] as const
export const CLINIC_SETTINGS_QUERY_KEY = ['clinic-settings'] as const

/**
 * How long a revoked capability may stay on screen.
 *
 * Deliberately shorter than Today's five minutes, because it answers a
 * different question. Today asks "what day is it", where being wrong costs a
 * stale row. This asks "is this person still allowed to see this", where being
 * wrong costs a screen asserting a patient's records to an account that no
 * longer has access.
 *
 * The interval only does work for someone AT the machine in continuous use
 * whose access changes mid-shift — a role change, a shift ending, an account
 * being restricted. Anyone who walks away and comes back is covered by the
 * focus refetch below and never waits for this timer at all.
 */
export const SESSION_REFRESH_MS = 60_000

/**
 * The single source of "is there a session, and whose". Both halves are
 * required. `/auth/session` resolves both from one server authorization
 * snapshot, so the shell cannot paint a header with half an identity or mix
 * identity and capabilities from two requests.
 */
export function sessionQueryOptions() {
  return queryOptions({
    queryKey: SESSION_QUERY_KEY,
    queryFn: (): Promise<AccountSession> => getSessionSnapshot(),
    // A 401 is an answer, not a transient fault; retrying it just delays the
    // redirect to login.
    retry: false,
    // Navigating between pages should not re-ask who you are; capabilities
    // changing underneath you should. That is what the two settings below do,
    // and why this one stays.
    staleTime: 30_000,
    // 'always' rather than true, on purpose. With a 30s staleTime, `true` would
    // skip the refetch for anyone returning to the tab inside that window —
    // and coming back to the tab is exactly the moment to re-ask whether this
    // account may still see what is on screen.
    refetchOnWindowFocus: 'always',
    // Not `refetchIntervalInBackground`, also on purpose: a hidden tab polls
    // nothing, and the focus refetch above covers the moment it comes back.
    refetchInterval: SESSION_REFRESH_MS,
    // Both are set here rather than on the QueryClient. Flipping the global
    // default would put the patient list, the timeline and a schedule day
    // someone deliberately chose on the same clock, which is a quiet change
    // that surprises whoever meets it several units later.
  })
}

export function useSession() {
  return useQuery({ ...sessionQueryOptions(), select: (session) => hasOrganization(session) ? session : undefined })
}

export function useAccountSession() {
  return useQuery(sessionQueryOptions())
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: async () => {
      // Refetch rather than seed from the login response: the complete session
      // snapshot is still needed, and one code path is easier to reason about.
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => logout(),
    onSettled: () => {
      // Clear every cached query, not just the session: anything fetched while
      // authenticated must not survive for a back-navigation to re-render.
      browserSessionContext.invalidate()
      notifySessionContextChange()
      queryClient.clear()
    },
  })
}

export function useUpdateAccountLanguage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (language: Locale) => updateAccountLanguage(language),
    retry: false,
    onSuccess: (context) => {
      queryClient.setQueryData<AccountSession>(SESSION_QUERY_KEY, (session) =>
        session ? { ...session, context } : session,
      )
    },
  })
}

export function useSwitchClinic(beforeReconcile?: () => Promise<void>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clinicId: string) => switchActiveClinic(clinicId),
    retry: false,
    onSuccess: async () => {
      // Leave the old resource while transport is still fenced. Reconciliation
      // may then remount the workspace without reading that id in a new clinic.
      await beforeReconcile?.()
      await reconcileSessionContext(queryClient)
    },
  })
}

export function useCreateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOrganizationInput) => createOrganization(input),
    retry: false,
    onSuccess: async () => {
      await reconcileSessionContext(queryClient)
    },
  })
}

export async function reconcileSessionContext(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries()
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] })
  const session = await getSessionSnapshot(true)
  queryClient.setQueryData<AccountSession>(SESSION_QUERY_KEY, session)
  browserSessionContext.install(session.context.sessionContext)
}

export function useDeveloperElevation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { mode: DomainValues.DeveloperElevationMode; reason: string }) =>
      activateDeveloperElevation(input),
    retry: false,
    onSuccess: async (context) => {
      replaceAuthorityContext(queryClient, context)
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
    },
  })
}

export function useDeactivateDeveloperElevation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deactivateDeveloperElevation,
    retry: false,
    onSuccess: async (context) => {
      replaceAuthorityContext(queryClient, context)
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
    },
  })
}

/**
 * A clinic or elevation transition changes the authority behind every cached
 * product response. Remove those responses synchronously and install the
 * server-returned context before re-reading the full identity/session pair.
 */
function replaceAuthorityContext(queryClient: QueryClient, context: AccountSession['context']) {
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
  })
  queryClient.setQueryData<AccountSession>(SESSION_QUERY_KEY, (session) =>
    session ? { ...session, context } : session,
  )
}

/**
 * The clinic's editable settings, read from the server every time.
 *
 * Separate from the session query even though `/app-context` carries the same
 * operational zone and formats: only this read carries the `revision` and
 * `updatedAt` a write has to quote, and folding the two would make every screen
 * in the workspace re-read settings evidence it never renders.
 *
 * No `staleTime`, no interval and no focus policy of its own. This is a form's
 * baseline, not a surface that follows the clock, and a refetch arriving under
 * an open form would replace the revision the clinician is about to save
 * against. A concurrent change is caught by the revision instead, which is what
 * the revision is for.
 */
export function clinicSettingsQueryOptions() {
  return queryOptions({
    queryKey: CLINIC_SETTINGS_QUERY_KEY,
    queryFn: (): Promise<ClinicSettings> => getClinicSettings(),
    retry: false,
  })
}

/** Unconditional: the route guard above this read owns the capability gate. */
export function useClinicSettings() {
  return useQuery(clinicSettingsQueryOptions())
}

export function useUpdateClinicSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateClinicSettingsInput) => updateClinicSettings(input),
    retry: false,
    onSuccess: (settings) => {
      writeClinicSettings(queryClient, settings)
    },
  })
}

/**
 * Re-read the settings after a stale-revision refusal.
 *
 * READ DIRECTLY, NOT THROUGH `fetchQuery`. `fetchQuery` writes a FAILURE into
 * the same cache key the open form reads, so a failed recovery would replace
 * the half-edited form with the section's error panel — destroying the work the
 * recovery exists to protect. A success lands in both caches through the one
 * writer below; a failure answers null and the editor keeps what was typed.
 */
export function useClinicSettingsReload() {
  return useResourceReload(
    () => getClinicSettings(),
    (queryClient, fresh) => writeClinicSettings(queryClient, fresh),
  )
}

/**
 * One place that lands a server answer, so a read and a write cannot leave the
 * session and the settings caches disagreeing about the same clinic.
 *
 * Operational settings update the session's formatting context while preserving
 * its separately versioned operating identity. A reload reads the server again.
 */
function writeClinicSettings(queryClient: QueryClient, settings: ClinicSettings) {
  queryClient.setQueryData<ClinicSettings>(CLINIC_SETTINGS_QUERY_KEY, settings)
  queryClient.setQueryData<AccountSession>(SESSION_QUERY_KEY, (session) =>
    hasOrganization(session) && session.context.clinic.id === settings.id
      ? {
          ...session,
          context: {
            ...session.context,
            clinic: {
              ...session.context.clinic,
              id: settings.id,
              timeZone: settings.timeZone,
              calendarCountryCode: settings.calendarCountryCode,
              calendarSubdivisionCode: settings.calendarSubdivisionCode,
              dateFormat: settings.dateFormat,
              timeFormat: settings.timeFormat,
              numberFormat: settings.numberFormat,
              diagnosisTerminology: settings.diagnosisTerminology,
            },
          },
        }
      : session,
  )
}
